const SIZE_SYSTEM_TO_TAG = {
    letter: "INT",
    eu: "EU",
    us: "US",
    jp: "JP",
}

const TAG_TO_SIZE_SYSTEM = {
    INT: "letter",
    EU: "eu",
    IT: "eu",
    US: "us",
    JP: "jp",
}

function resolveSizeSystemFromTags(tags) {
    for (let i = 0; i < tags.length; i++) {
        const normalized = String(tags[i]).toUpperCase()
        const system = TAG_TO_SIZE_SYSTEM[normalized]

        if (system) {
            return system
        }
    }

    return null
}

function getTagsForSizeSystem(system) {
    switch (system) {
        case "letter":
            return ["INT"]
        case "eu":
            return ["EU", "IT"]
        case "us":
            return ["US"]
        case "jp":
            return ["JP"]
        default:
            return []
    }
}

function normalizeSizeSystemForCategory(system, categoryMetafield) {
    if (!system || (categoryMetafield === "Shoes" && (system === "letter" || system === "jp"))) {
        return "eu"
    }

    return system
}

const SIZE_AVAILABILITY_CACHE_MAX = 32
const sizeAvailabilityCache = new Map()
const sizeAvailabilityInflight = new Map()
const sectionFetchCache = new Map()
const sectionFetchInflight = new Map()

function cacheSetLimited(map, key, value, maxSize) {
    if (map.has(key)) {
        map.delete(key)
    }

    map.set(key, value)

    while (map.size > maxSize) {
        const oldestKey = map.keys().next().value
        map.delete(oldestKey)
    }
}

function extractAvailabilityMap(doc) {
    const availabilityByValue = new Map()

    doc.querySelectorAll('li[data-size-kind] input[name="filter.v.option.size"]').forEach((input) => {
        availabilityByValue.set(String(input.value), !input.disabled)
    })

    return availabilityByValue
}

class FacetsFilterElement extends HTMLElement {
    connectedCallback() {
        this._onSizeSystemRefresh = this._onSizeSystemRefresh.bind(this)
        this.addEventListener("size-system:refresh", this._onSizeSystemRefresh)
        this.getTargetAttributes()
        this._availabilityRequestId = 0
        this._loadingCount = 0

        const sectionCacheKey = this.buildFiltersFetchUrl({ sizeSystem: "eu", applyRegionTags: false }).toString()
        const hasCachedSection = sectionFetchCache.has(sectionCacheKey)

        let sectionLoading = false

        if (!hasCachedSection) {
            sectionLoading = true
            this.setSizeFiltersLoading(true)
        }

        this.fetchFiltersSection({ sizeSystem: "eu", applyRegionTags: false })
            .then((parsedDocument) => {
                const elements = Array.from(parsedDocument.body.children)

                if (elements.length > 0) {
                    this.replaceChildren(...elements)
                } else {
                    this.replaceChildren()
                }

                this.initSizeSystemSwitcher()
                this.dispatchEvent(new CustomEvent("facets-filter:updated", { bubbles: true }))
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    console.error("Failed to fetch filter section:", error)
                }
            })
            .finally(() => {
                if (sectionLoading) {
                    this.setSizeFiltersLoading(false)
                }
            })
    }

    static clearSizeCaches() {
        sizeAvailabilityCache.clear()
        sizeAvailabilityInflight.clear()
        sectionFetchCache.clear()
        sectionFetchInflight.clear()
    }

    getSizeContentRoot() {
        return this.querySelector(".Collapsible__Content--size")
    }

    ensureSizeFiltersLoader() {
        const contentRoot = this.getSizeContentRoot()

        if (!contentRoot) {
            return null
        }

        let loader = contentRoot.querySelector(".SizeFiltersLoader")

        if (!loader) {
            loader = document.createElement("div")
            loader.className = "SizeFiltersLoader"
            loader.setAttribute("aria-hidden", "true")
            loader.innerHTML = '<span class="SizeFiltersLoader__Spinner"></span>'
            contentRoot.appendChild(loader)
        }

        return contentRoot
    }

    setSizeFiltersLoading(isLoading) {
        const contentRoot = this.ensureSizeFiltersLoader() || this.getSizeContentRoot()

        if (!contentRoot) {
            return
        }

        if (isLoading) {
            this._loadingCount += 1
        } else {
            this._loadingCount = Math.max(0, this._loadingCount - 1)
        }

        const showLoader = this._loadingCount > 0
        contentRoot.classList.toggle("is-loading", showLoader)
        contentRoot.setAttribute("aria-busy", showLoader ? "true" : "false")
    }

    normalizeSizeSystem(system) {
        return normalizeSizeSystemForCategory(system, this.categoryMetafield)
    }

    normalizeSizeSystemSelect(select) {
        if (!select) {
            return "eu"
        }

        const system = this.normalizeSizeSystem(select.value || "eu")
        const hasOption = select.querySelector(`option[value="${system}"]`)

        if (hasOption) {
            select.value = system
        } else if (select.querySelector('option[value="eu"]')) {
            select.value = "eu"
        }

        return select.value || "eu"
    }

    getActiveSizeSystemFromSelect() {
        const select = this.querySelector("[data-size-system-select]")

        if (!select) {
            return null
        }

        return this.normalizeSizeSystem(select.value || "eu")
    }

    getCollectionFetchPath() {
        const pathname = (window.location.pathname || "").trim()
        const requestPath = (this.collectionRequestPath || "").trim()
        const parentPath = (this.parentCollectionPath || "").trim()

        if (requestPath && parentPath && pathname === parentPath && requestPath !== parentPath) {
            return requestPath
        }

        if (pathname) {
            return pathname
        }

        if (requestPath) {
            return requestPath
        }

        return parentPath || "/"
    }

    applySizeRegionTagsToUrl(fetchUrl, sizeSystem) {
        FacetsFilterElement.applyRegionTagsToSearchParams(
            fetchUrl.searchParams,
            sizeSystem || this.getActiveSizeSystemFromSelect() || "eu",
            this.categoryMetafield
        )
    }

    buildFiltersFetchUrl(options = {}) {
        const fetchPath = this.getCollectionFetchPath()
        const fetchUrl = new URL(fetchPath, window.location.origin)
        const currentParams = new URLSearchParams(window.location.search)
        const addedParams = new Map()
        const sizeSystem = options.sizeSystem || this.getActiveSizeSystemFromSelect() || "eu"

        currentParams.forEach((value, key) => {
            if (key === "section_id" || key === "sections" || key === "page" || key === "filter.p.tag") {
                return
            }

            if (!addedParams.has(key)) {
                addedParams.set(key, new Set())
            }

            const values = addedParams.get(key)

            if (values.has(value)) {
                return
            }

            values.add(value)
            fetchUrl.searchParams.append(key, value)
        })

        if (this.categoryMetafield) {
            fetchUrl.searchParams.delete("filter.p.m.custom.categories")
            fetchUrl.searchParams.append("filter.p.m.custom.categories", this.categoryMetafield)
        }

        const sizeGender = this.getSizeGender()

        if (sizeGender === "mens" || sizeGender === "womens") {
            fetchUrl.searchParams.set("size_gender", sizeGender)
        }

        let shouldAppendAvailability = false
        currentParams.forEach((value, key) => {
            if (key === "section_id" || key === "sections" || key === "page" || key === "filter.p.tag") {
                return
            }

            if (key.indexOf("filter.") === 0 && key !== "filter.v.availability") {
                shouldAppendAvailability = true
            }
        })

        if (shouldAppendAvailability) {
            fetchUrl.searchParams.delete("filter.v.availability")
            fetchUrl.searchParams.append("filter.v.availability", "1")
        }

        const hasAppliedSizes = currentParams.getAll("filter.v.option.size").length > 0
        let applyRegionTags = options.applyRegionTags

        if (applyRegionTags === undefined) {
            applyRegionTags = Boolean(this.categoryMetafield && hasAppliedSizes)
        }

        if (applyRegionTags) {
            this.applySizeRegionTagsToUrl(fetchUrl, sizeSystem)
        }

        fetchUrl.searchParams.set("sections", "filters-section")

        return fetchUrl
    }

    parseFiltersSectionHtml(responseText) {
        let htmlString = responseText

        try {
            const json = JSON.parse(responseText)

            if (json && typeof json === "object") {
                htmlString = Object.values(json).find((value) => typeof value === "string") || responseText
            }
        } catch (_error) {
            // Non-JSON response; keep original response text as HTML string.
        }

        htmlString = htmlString.replace("[[Accordion_title]]", this.headingName)
        htmlString = htmlString.replaceAll("[[Category_prefix]]", this.categoryPrefix)
        htmlString = htmlString.replaceAll("@@@@", this.instancePrefix)

        return new DOMParser().parseFromString(htmlString, "text/html")
    }

    fetchFiltersSection(options = {}) {
        const fetchUrl = this.buildFiltersFetchUrl(options)
        const cacheKey = fetchUrl.toString()

        if (sectionFetchCache.has(cacheKey)) {
            return Promise.resolve(this.parseFiltersSectionHtml(sectionFetchCache.get(cacheKey)))
        }

        if (sectionFetchInflight.has(cacheKey)) {
            return sectionFetchInflight.get(cacheKey).then((responseText) => this.parseFiltersSectionHtml(responseText))
        }

        if (this._sectionAbortController) {
            this._sectionAbortController.abort()
        }

        this._sectionAbortController = new AbortController()

        const request = fetch(fetchUrl.toString(), { signal: this._sectionAbortController.signal })
            .then((res) => res.text())
            .then((responseText) => {
                cacheSetLimited(sectionFetchCache, cacheKey, responseText, SIZE_AVAILABILITY_CACHE_MAX)
                return responseText
            })
            .finally(() => {
                sectionFetchInflight.delete(cacheKey)
            })

        sectionFetchInflight.set(cacheKey, request)

        return request.then((responseText) => this.parseFiltersSectionHtml(responseText))
    }

    setSizeItemUnavailable(item, input, unavailable) {
        item.classList.toggle("Linklist__Item--unavailable", unavailable)

        const label = item.querySelector(".HorizontalList__Label")

        if (label) {
            label.classList.toggle("HorizontalList__Label--unavailable", unavailable)
        }

        if (unavailable && !input.checked) {
            input.disabled = true
            input.setAttribute("aria-disabled", "true")
        } else {
            input.disabled = false
            input.removeAttribute("aria-disabled")
        }
    }

    markAmbiguousSizesPending(items) {
        items.forEach((item) => {
            if (item.getAttribute("data-size-ambiguous") !== "true") {
                return
            }

            const input = item.querySelector('input[type="checkbox"]')

            if (!input || input.checked) {
                return
            }

            this.setSizeItemUnavailable(item, input, true)
        })
    }

    applySizeAvailabilityFromMap(availabilityByValue, selectedSystem) {
        const numericItems = Array.from(this.querySelectorAll('li[data-size-kind="numeric"]'))
        const letterItems = Array.from(this.querySelectorAll('li[data-size-kind="letter"]'))
        const items = selectedSystem === "letter" ? letterItems : numericItems

        items.forEach((item) => {
            if (item.style.display === "none") {
                return
            }

            const input = item.querySelector('input[type="checkbox"]')

            if (!input) {
                return
            }

            const systems = (item.getAttribute("data-size-systems") || "")
                .trim()
                .split(/\s+/)
                .filter(Boolean)
            const isVisibleForSystem =
                selectedSystem === "letter" || !systems.length || systems.includes(selectedSystem)

            if (!isVisibleForSystem) {
                return
            }

            const isAvailable = availabilityByValue.get(String(input.value))

            if (isAvailable === undefined) {
                this.setSizeItemUnavailable(item, input, !input.checked)
                return
            }

            this.setSizeItemUnavailable(item, input, !input.checked && !isAvailable)
        })

        if (selectedSystem !== "letter") {
            this.applyFootwearGenderScopes(numericItems)
        }
    }

    applySizeAvailabilityFromDocument(doc, selectedSystem) {
        this.applySizeAvailabilityFromMap(extractAvailabilityMap(doc), selectedSystem)
    }

    fetchSizeAvailability(system) {
        const fetchUrl = this.buildFiltersFetchUrl({ sizeSystem: system, applyRegionTags: true })
        const cacheKey = fetchUrl.toString()

        if (sizeAvailabilityCache.has(cacheKey)) {
            return Promise.resolve(sizeAvailabilityCache.get(cacheKey))
        }

        if (sizeAvailabilityInflight.has(cacheKey)) {
            return sizeAvailabilityInflight.get(cacheKey)
        }

        if (this._availabilityAbortController) {
            this._availabilityAbortController.abort()
        }

        this._availabilityAbortController = new AbortController()

        const request = fetch(fetchUrl.toString(), { signal: this._availabilityAbortController.signal })
            .then((res) => res.text())
            .then((responseText) => {
                const availabilityMap = extractAvailabilityMap(this.parseFiltersSectionHtml(responseText))
                cacheSetLimited(sizeAvailabilityCache, cacheKey, availabilityMap, SIZE_AVAILABILITY_CACHE_MAX)
                return availabilityMap
            })
            .finally(() => {
                sizeAvailabilityInflight.delete(cacheKey)
            })

        sizeAvailabilityInflight.set(cacheKey, request)

        return request
    }

    refreshSizeAvailability(system) {
        system = this.normalizeSizeSystem(system)

        if (!system) {
            return Promise.resolve()
        }

        const requestId = ++this._availabilityRequestId
        const cacheKey = this.buildFiltersFetchUrl({ sizeSystem: system }).toString()
        const hasCachedAvailability = sizeAvailabilityCache.has(cacheKey)
        let availabilityLoading = false

        if (!hasCachedAvailability) {
            availabilityLoading = true
            this.setSizeFiltersLoading(true)
        }

        return this.fetchSizeAvailability(system)
            .then((availabilityMap) => {
                if (requestId !== this._availabilityRequestId) {
                    return
                }

                this.applySizeAvailabilityFromMap(availabilityMap, system)
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    console.error("Failed to refresh size availability:", error)
                }
            })
            .finally(() => {
                if (availabilityLoading) {
                    this.setSizeFiltersLoading(false)
                }
            })
    }

    getTargetAttributes() {
        this.parentCollectionPath = this.getAttribute("data-parent-collection-path") || "";
        this.collectionRequestPath = this.getAttribute("data-request-path") || "";
        this.currentTags = this.getAttribute("data-current-tags") || "";
        this.collectionHandle = this.getAttribute("data-collection-handle") || "";
        this.categoryMetafield = this.getAttribute("data-category-metafield") || "";
        this.headingName = this.getAttribute("data-heading-name") || "";
        this.filterCategory = this.getAttribute("data-filter-category") || "";
        this.categoryPrefix = this.getAttribute("data-category-prefix") || this.filterCategory || "size";
        this.instancePrefix = this.getAttribute("data-filter-instance") || "facet";
        this.sizeGender = this.getAttribute("data-size-gender") || "";
    }

    getSizeGender() {
        const fromAttr = (this.sizeGender || "").toLowerCase().trim()
        if (fromAttr === "mens" || fromAttr === "womens") {
            return fromAttr
        }

        const handle = (this.collectionHandle || "").toLowerCase()
        if (handle === "womens" || handle.includes("womens-") || handle.includes("-womens")) {
            return "womens"
        }
        if (handle === "mens" || handle.includes("mens-") || handle.includes("-mens")) {
            return "mens"
        }

        const requestPath = (this.collectionRequestPath || window.location.pathname || "").toLowerCase()
        const pathLast = requestPath.split("/").filter(Boolean).pop() || ""

        if (pathLast === "womens") {
            return "womens"
        }

        if (pathLast === "mens") {
            return "mens"
        }

        return "both"
    }

    isFootwearSizeVisibleForGender(item) {
        if (this.categoryMetafield !== "Shoes") {
            return true
        }

        const scopes = (item.getAttribute("data-size-genders") || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean)

        if (!scopes.length) {
            return true
        }

        const gender = this.getSizeGender()

        if (scopes.includes("both")) {
            return true
        }

        if (gender === "both") {
            return scopes.includes("mens") || scopes.includes("womens")
        }

        return scopes.includes(gender)
    }

    applyFootwearGenderScopes(numericItems) {
        if (this.categoryMetafield !== "Shoes") {
            return
        }

        numericItems.forEach((item) => {
            if (this.isFootwearSizeVisibleForGender(item)) {
                return
            }

            item.style.display = "none"
            const input = item.querySelector('input[type="checkbox"]')

            if (input && input.checked) {
                input.checked = false
            }
        })
    }

    getFiltersRoot() {
        return this.closest(".CollectionFilters") || document
    }

    syncSizeSystemSelects(select) {
        const facetsElement = select.closest("facets-filter-element")
        const filterCategory = facetsElement && facetsElement.getAttribute("data-filter-category")

        if (!filterCategory) {
            return
        }

        const filtersRoot = this.getFiltersRoot()

        filtersRoot
            .querySelectorAll(`facets-filter-element[data-filter-category="${filterCategory}"] [data-size-system-select]`)
            .forEach((otherSelect) => {
                if (otherSelect === select) {
                    return
                }

                if (otherSelect.querySelector(`option[value="${select.value}"]`)) {
                    otherSelect.value = select.value
                }
            })
    }

    _onSizeSystemRefresh() {
        const select = this.querySelector("[data-size-system-select]")
        if (!select || !this._runSizeSystemChange) {
            return
        }

        this._runSizeSystemChange()
    }

    initSizeSystemSwitcher() {
        const lists = this.querySelectorAll(".Collapsible__Content--size .Linklist")
        if (!lists.length) return

        const hideJpSizes = this.categoryMetafield === "Shoes"

        lists.forEach((list) => {
            const select = list.querySelector("[data-size-system-select]")
            if (!select) return

            const letterItems = Array.from(list.querySelectorAll('li[data-size-kind="letter"]'))
            const numericItems = Array.from(list.querySelectorAll('li[data-size-kind="numeric"]'))

                if (hideJpSizes) {
                    const jpOption = select.querySelector('option[value="jp"]')
                    if (jpOption) {
                        jpOption.remove()
                    }

                    const letterOption = select.querySelector('option[value="letter"]')
                    if (letterOption) {
                        letterOption.remove()
                    }

                    if (select.value === "jp" || select.value === "letter") {
                        select.value = "eu"
                    }
                }

            const setCheckedInputsHidden = (items) => {
                items.forEach((item) => {
                    const input = item.querySelector('input[type="checkbox"]')
                    if (input && input.checked) {
                        input.checked = false
                    }
                })
            }

            const runSizeSystemChange = () => {
                let selectedSystem = this.normalizeSizeSystemSelect(select)

                this.syncSizeSystemSelects(select)

                if (selectedSystem === "letter") {
                    letterItems.forEach((item) => {
                        item.style.display = ""
                    })
                    setCheckedInputsHidden(numericItems)
                    numericItems.forEach((item) => {
                        item.style.display = "none"
                    })
                    this.markAmbiguousSizesPending(letterItems)
                    this.refreshSizeAvailability("letter")
                    return
                }

                letterItems.forEach((item) => {
                    item.style.display = "none"
                })
                setCheckedInputsHidden(letterItems)

                numericItems.forEach((item) => {
                    if (!this.isFootwearSizeVisibleForGender(item)) {
                        item.style.display = "none"
                        const hiddenInput = item.querySelector('input[type="checkbox"]')

                        if (hiddenInput && hiddenInput.checked) {
                            hiddenInput.checked = false
                        }

                        return
                    }

                    let systems = (item.getAttribute("data-size-systems") || "")
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)

                    if (hideJpSizes) {
                        systems = systems.filter((system) => system !== "jp")

                        if (!systems.length) {
                            item.style.display = "none"
                            const input = item.querySelector('input[type="checkbox"]')
                            if (input && input.checked) {
                                input.checked = false
                            }
                            return
                        }
                    }

                    item.style.display = systems.includes(selectedSystem) ? "" : "none"
                })

                const visibleNumericItems = numericItems.filter((item) => item.style.display !== "none")
                this.markAmbiguousSizesPending(visibleNumericItems)
                this.refreshSizeAvailability(selectedSystem)
            }

            this._runSizeSystemChange = runSizeSystemChange

            const hasCheckedLetter = letterItems.some((item) => {
                const input = item.querySelector('input[type="checkbox"]')
                return input && input.checked
            })

            const getActiveNumericSystem = () => {
                for (const item of numericItems) {
                    const input = item.querySelector('input[type="checkbox"]')
                    if (!input || !input.checked) {
                        continue
                    }

                    let systems = (item.getAttribute("data-size-systems") || "")
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)

                    if (hideJpSizes) {
                        systems = systems.filter((system) => system !== "jp")
                    }

                    return systems[0] || null
                }

                return null
            }

            if (hasCheckedLetter && !hideJpSizes && select.querySelector('option[value="letter"]')) {
                select.value = "letter"
            } else {
                const activeNumericSystem = getActiveNumericSystem()

                if (activeNumericSystem && select.querySelector(`option[value="${activeNumericSystem}"]`)) {
                    select.value = activeNumericSystem
                } else if (!hideJpSizes && select.querySelector('option[value="letter"]')) {
                    select.value = "letter"
                } else if (select.querySelector('option[value="eu"]')) {
                    select.value = "eu"
                }
            }

            this.normalizeSizeSystemSelect(select)

            if (!select.dataset.sizeSystemBound) {
                select.dataset.sizeSystemBound = "true"
                select.addEventListener("change", runSizeSystemChange)
            }

            runSizeSystemChange()
        })
    }
}

FacetsFilterElement.getTargetFacetsFromUrl = function (scope, urlParams) {
    const sizeParam = "filter.v.option.size"
    const categoriesParam = "filter.p.m.custom.categories"
    const activeSizes = urlParams.getAll(sizeParam)
    const activeCategories = urlParams.getAll(categoriesParam)
    const targetFacets = []

    if (activeCategories.length > 0) {
        const categoryValue = activeCategories[activeCategories.length - 1]

        scope.querySelectorAll(`facets-filter-element[data-category-metafield="${categoryValue}"]`).forEach((element) => {
            targetFacets.push(element)
        })
    } else if (activeSizes.length > 0) {
        scope.querySelectorAll("facets-filter-element").forEach((element) => {
            if (!element.getAttribute("data-category-metafield")) {
                targetFacets.push(element)
            }
        })
    }

    return targetFacets
}

FacetsFilterElement.getTagsForSizeSystem = getTagsForSizeSystem

FacetsFilterElement.applyRegionTagsToSearchParams = function (searchParams, sizeSystem, categoryMetafield) {
    const normalizedSystem = normalizeSizeSystemForCategory(sizeSystem || "eu", categoryMetafield || "")

    searchParams.delete("filter.p.tag")

    getTagsForSizeSystem(normalizedSystem).forEach((tag) => {
        searchParams.append("filter.p.tag", tag)
    })
}

FacetsFilterElement.getActiveFacetCategoryFromScope = function (scope) {
    const root = scope || document
    let activeCategory = null

    root.querySelectorAll("facets-filter-element").forEach((facetsElement) => {
        if (facetsElement.querySelector('input[name="filter.v.option.size"]:checked')) {
            activeCategory = facetsElement.getAttribute("data-filter-category") || ""
        }
    })

    return activeCategory
}

FacetsFilterElement.applyRegionTagsToUrl = function (url, options = {}) {
    const urlObject = url instanceof URL ? url : new URL(url, window.location.origin)
    const scope = options.scope || document
    const activeCategory =
        typeof options.activeCategory === "string" ? options.activeCategory : FacetsFilterElement.getActiveFacetCategoryFromScope(scope)
    const targetElements = activeCategory
        ? scope.querySelectorAll(`facets-filter-element[data-filter-category="${activeCategory}"]`)
        : scope.querySelectorAll("facets-filter-element")
    const facetsElement = Array.from(targetElements).find((element) => element.querySelector("[data-size-system-select]"))

    if (!facetsElement) {
        return urlObject
    }

    const select = facetsElement.querySelector("[data-size-system-select]")

    FacetsFilterElement.applyRegionTagsToSearchParams(
        urlObject.searchParams,
        select.value,
        facetsElement.getAttribute("data-category-metafield") || ""
    )

    return urlObject
}

FacetsFilterElement.syncSizeSystemFromUrl = function (root, url) {
    const scope = root || document
    const urlParams = new URLSearchParams(new URL(url, window.location.origin).search)
    const hasCheckedSizes = urlParams.getAll("filter.v.option.size").length > 0

    const refreshAll = () => {
        scope.querySelectorAll("facets-filter-element").forEach((element) => {
            element.dispatchEvent(new CustomEvent("size-system:refresh"))
        })
    }

    if (!hasCheckedSizes) {
        refreshAll()
        return
    }

    const tags = urlParams.getAll("filter.p.tag")
    const system = resolveSizeSystemFromTags(tags)

    const targetFacets = FacetsFilterElement.getTargetFacetsFromUrl(scope, urlParams)

    if (system && targetFacets.length > 0) {
        targetFacets.forEach((element) => {
            const select = element.querySelector("[data-size-system-select]")

            if (!select) {
                return
            }

            const resolvedSystem =
                element.getAttribute("data-category-metafield") === "Shoes"
                    ? system === "letter" || system === "jp"
                        ? "eu"
                        : system
                    : system

            if (select.querySelector(`option[value="${resolvedSystem}"]`)) {
                select.value = resolvedSystem
            } else {
                element.normalizeSizeSystemSelect(select)
            }
        })
    }

    refreshAll()
}

if (!customElements.get("facets-filter-element")) {
    customElements.define("facets-filter-element", FacetsFilterElement)
}